/* istanbul ignore file */ /* plugin extension code */

import { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useBreakpointHelpers } from '@sweetgreen/garnish';

import { checkIfAccountTabScreen } from '../../../navigation';
import { useKustomerAvailability, useKustomerToken } from '../hooks';
import {
  useCloseKustomer,
  useLoginKustomer,
  useOpenKustomer,
  useStartKustomer,
  useStopKustomer,
  useStopKustomerOnMobileClose,
} from './kustomer-web-integration';
import {
  addRegionRoleToKustomerFrame,
  useLoadKustomer,
} from './kustomer-web-scripts';
export { useLogoutKustomer } from './kustomer-web-integration';

export const useKustomer = () => {
  const isKustomerEnabled = useKustomerAvailability();
  const { kustomerToken, fetchKustomerToken } =
    useKustomerToken(isKustomerEnabled);

  // ─── State ───────────────────────────────────────────────────────────

  const [openingKustomer, setOpeningKustomer] = useState(false);
  const [isLoggedInKustomer, setIsLoggedInKustomer] = useState(false);

  // ─── Kustomer Integration ────────────────────────────────────────────

  const loadKustomer = useLoadKustomer();
  const startKustomer = useStartKustomer();
  const openKustomer = useOpenKustomer();
  const loginKustomer = useLoginKustomer();
  const stopKustomerOnMobileClose = useStopKustomerOnMobileClose();

  // ─── Callbacks ───────────────────────────────────────────────────────
  // Kustomer will auto open and not resolve if it was previously opened.
  // To prevent getting stuck, we trigger open again on the open callback.

  const onKustomerOpen = useCallback(() => {
    setOpeningKustomer(false);
    addRegionRoleToKustomerFrame();
    stopKustomerOnMobileClose();
    void openKustomer();
  }, [openKustomer, stopKustomerOnMobileClose]);

  const loadAndStartKustomer = useCallback(async () => {
    await loadKustomer();
    await startKustomer();

    if (!kustomerToken) fetchKustomerToken();
  }, [kustomerToken, loadKustomer, startKustomer, fetchKustomerToken]);

  const loadAndOpenKustomer = useCallback(async () => {
    setOpeningKustomer(true);
    void loadAndStartKustomer().then(onKustomerOpen);
    void openKustomer().then(onKustomerOpen);
  }, [loadAndStartKustomer, onKustomerOpen, openKustomer]);

  const loginToKustomer = useCallback(() => {
    if (!kustomerToken || isLoggedInKustomer) return;

    (async () => {
      try {
        await loginKustomer({ kustomerToken });
        setIsLoggedInKustomer(true);
      } catch {
        setIsLoggedInKustomer(false);
      }
    })();
  }, [isLoggedInKustomer, kustomerToken, loginKustomer]);

  useFocusEffect(loginToKustomer);

  return {
    isKustomerEnabled,
    openingKustomer,
    start: loadAndStartKustomer,
    open: loadAndOpenKustomer,
  };
};

export const usePauseKustomer = () => {
  const isKustomerEnabled = useKustomerAvailability();
  const { isMobile } = useBreakpointHelpers();

  const closeKustomer = useCloseKustomer();
  const stopKustomer = useStopKustomer();

  return useCallback(
    (route: string) => {
      const isInAccountTab = checkIfAccountTabScreen(route);
      const shouldRemove = !isInAccountTab || isMobile || !isKustomerEnabled;

      if (!shouldRemove) return;

      void closeKustomer();
      void stopKustomer();
    },
    [isMobile, isKustomerEnabled, closeKustomer, stopKustomer],
  );
};
